import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {PartnerProductDgSearchResults} from "../models/datagrid-search-results/partner-product.dg-search-results.model";
import {PartnerProduct} from "../models/partner-product.model";
import {ProductCategoriesForSpecies} from "../models/product-categories-for-species.model";

/**
 * Serwis warstwy danych zarządzający asortymentem (Katalogiem Produktów) dostarczanym
 * przez zewnętrznych Partnerów platformy. Odpowiada za wyświetlanie, wyszukiwanie,
 * kategoryzację oraz synchronizację produktów w module E-commerce.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerProductService implements DataGridServiceInterface<Observable<PartnerProductDgSearchResults>> {

  constructor(private apiService: ApiService, public commonUtils: CommonUtils) {
  }

  /**
   * Pobiera paginowaną listę produktów partnerskich do zasilenia tabeli DataGrid.
   *
   * @param limit Maksymalna liczba rekordów na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. 'ASC', 'DESC').
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga decydująca o widoczności usuniętych/wygaszonych produktów.
   * @returns Observable emitujący ustandaryzowany obiekt wyników dla komponentu tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerProductDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-products/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const partnerProductDgSearchResults = new PartnerProductDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerProduct(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return partnerProductDgSearchResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje i filtruje asortyment partnerski w oparciu o zaawansowane parametry.
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry wyszukiwania.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę produktów dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerProductDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>('/dashboard/partner-products/search', data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse.response.results);
        return new PartnerProductDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerProduct(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełne szczegóły pojedynczego produktu.
   *
   * @param uuid Unikalny identyfikator produktu.
   * @returns Observable emitujący szczegółowy obiekt produktu.
   */
  getDetails(uuid: string): Observable<PartnerProduct> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-products/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<PartnerProduct>(new PartnerProduct(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Szybka wyszukiwarka tekstowa produktów w obrębie asortymentu konkretnego partnera.
   *
   * @param nameToFind Szukana fraza (nazwa produktu).
   * @param partnerId Identyfikator nadrzędnego partnera (właściciela asortymentu).
   * @returns Observable emitujący płaską tablicę dopasowanych produktów.
   */
  searchForProductByPhrase(nameToFind: string, partnerId: string) {
    const body = {
      phrase: nameToFind,
      releatedItemId: partnerId
    };
    return this.apiService.post<VetineoApiResponse>('/dashboard/partner-products/by-phrase', body, {}).pipe(take(1), map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedArrayCopyEver(new PartnerProduct(), [], vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Wymusza synchronizację asortymentu partnera.
   *
   * @param uuid Identyfikator partnera, którego asortyment ma zostać zaktualizowany.
   * @returns Observable emitujący flagę potwierdzającą przyjęcie zlecenia synchronizacji.
   */
  refreshProductsForPartner(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-products-reload/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return true;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalne, domenowe drzewo kategorii produktów z podziałem na gatunki zwierząt.
   *
   * @returns Observable emitujący strukturę kategorii powiązaną z gatunkami.
   */
  getCategories(): Observable<ProductCategoriesForSpecies[]> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/partner-product-categories').pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new ProductCategoriesForSpecies(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera konfigurację widoczności kategorii dla konkretnej placówki weterynaryjnej.
   *
   * @param vetcode Numeryczny identyfikator kliniki.
   * @returns Observable emitujący zserializowaną konfigurację kategorii (jako ciąg znaków).
   */
  getCategoriesConfig(vetcode): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-product-categories/${vetcode}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje nową konfigurację widoczności kategorii produktów dla konkretnej kliniki.
   *
   * @param clinicUUID Unikalny identyfikator kliniki.
   * @param categories Skonfigurowane i zserializowane do JSON string ustawienia kategorii.
   * @returns Observable potwierdzający zapis konfiguracji.
   */
  configureCategories(clinicUUID: string, categories: string) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/partner-product-categories/config', {clinicUUID, categories}, {}).pipe(take(1), map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors && vetineoApiResponse.response) {
        return true;
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Pobiera całą bazę asortymentową (bez stronicowania) danego partnera.
   *
   * @param partnerId ID partnera.
   * @returns Observable emitujący pełną listę asortymentu.
   */
  getAllPartnerProducts(partnerId: number): Observable<PartnerProduct[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-products-all/${partnerId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new PartnerProduct(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
